import { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { ROLE_LABELS } from "@/lib/constants";
import {
  LayoutDashboard, FilePlus2, Building2, Factory, ScanLine, ShieldCheck,
  Bell, LogOut, Map, ScrollText, Users, Menu, X,
} from "lucide-react";

const NAV = {
  COMMERCANT: [
    { to: "/app", label: "Mes dossiers", icon: LayoutDashboard },
    { to: "/app/nouvelle-demande", label: "Nouvelle demande", icon: FilePlus2 },
  ],
  ADMIN_MAIRIE: [
    { to: "/app", label: "Tableau de bord", icon: LayoutDashboard },
    { to: "/app/reglements", label: "Règlements", icon: ScrollText },
  ],
  AGENT_MAIRIE: [{ to: "/app", label: "Tableau de bord", icon: LayoutDashboard }],
  AGENT_CONTROLE: [{ to: "/app", label: "Scanner terrain", icon: ScanLine }],
  FABRICANT: [{ to: "/app", label: "Marketplace", icon: Factory }],
  SUPER_ADMIN: [{ to: "/app", label: "Administration", icon: ShieldCheck }],
};

const ROLE_ICON = {
  COMMERCANT: Building2, ADMIN_MAIRIE: Building2, AGENT_MAIRIE: Building2,
  AGENT_CONTROLE: ScanLine, FABRICANT: Factory, SUPER_ADMIN: ShieldCheck,
};

export default function Shell({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [notifs, setNotifs] = useState([]);
  const [openN, setOpenN] = useState(false);
  const [openMenu, setOpenMenu] = useState(false);

  const loadNotifs = () => api.get("/notifications").then((r) => setNotifs(r.data)).catch(() => {});
  useEffect(() => {
    loadNotifs();
    const t = setInterval(loadNotifs, 20000);
    return () => clearInterval(t);
  }, []);

  const unread = notifs.filter((n) => !n.read).length;
  const items = NAV[user?.role] || [];
  const RoleIcon = ROLE_ICON[user?.role] || Building2;

  const markAll = async () => {
    await api.post("/notifications/read-all");
    loadNotifs();
  };

  return (
    <div className="min-h-screen flex bg-[#F4F4F5]">
      {/* Sidebar */}
      <aside className={`fixed lg:static z-40 h-full w-64 bg-[#000091] text-white flex flex-col transition-transform ${openMenu ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="px-5 py-6 border-b border-white/15">
          <Link to="/app" className="flex items-center gap-2" data-testid="brand-link">
            <div className="w-8 h-8 bg-white flex items-center justify-center">
              <span className="text-[#000091] font-black text-lg">E</span>
            </div>
            <div>
              <div className="font-display font-black text-lg leading-none tracking-tight">ENSEIGNE</div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-white/60">Smart</div>
            </div>
          </Link>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {items.map((it) => {
            const Icon = it.icon;
            const active = loc.pathname === it.to;
            return (
              <Link
                key={it.to}
                to={it.to}
                data-testid={`nav-${it.label.toLowerCase().replace(/\s/g, "-")}`}
                onClick={() => setOpenMenu(false)}
                className={`flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors ${
                  active ? "bg-white text-[#000091]" : "text-white/80 hover:bg-white/10"
                }`}
              >
                <Icon size={18} /> {it.label}
              </Link>
            );
          })}
        </nav>
        <div className="p-4 border-t border-white/15">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-9 h-9 bg-white/10 flex items-center justify-center">
              <RoleIcon size={18} />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold truncate">{user?.name}</div>
              <div className="text-[10px] uppercase tracking-wider text-white/50">{ROLE_LABELS[user?.role]}</div>
            </div>
          </div>
          <button
            data-testid="logout-btn"
            onClick={() => { logout(); nav("/"); }}
            className="w-full flex items-center justify-center gap-2 py-2 text-sm border border-white/25 hover:bg-white/10 transition-colors"
          >
            <LogOut size={16} /> Déconnexion
          </button>
        </div>
      </aside>

      {openMenu && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setOpenMenu(false)} />}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 bg-white border-b border-border flex items-center justify-between px-4 lg:px-8 sticky top-0 z-20">
          <button className="lg:hidden" onClick={() => setOpenMenu(true)} data-testid="menu-toggle">
            <Menu size={22} />
          </button>
          <div className="hidden lg:block text-xs uppercase tracking-[0.2em] text-slate-500">
            Plateforme de gestion des enseignes commerciales
          </div>
          <div className="relative ml-auto">
            <button
              data-testid="notifications-btn"
              onClick={() => setOpenN(!openN)}
              className="relative w-10 h-10 flex items-center justify-center hover:bg-zinc-100 transition-colors"
            >
              <Bell size={20} />
              {unread > 0 && (
                <span className="absolute top-1 right-1 min-w-[18px] h-[18px] px-1 bg-[#E1000F] text-white text-[10px] font-bold flex items-center justify-center">
                  {unread}
                </span>
              )}
            </button>
            {openN && (
              <div className="absolute right-0 mt-2 w-80 bg-white border border-border shadow-lg z-50" data-testid="notifications-panel">
                <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                  <span className="font-semibold text-sm">Notifications</span>
                  <button onClick={markAll} className="text-xs text-[#000091] hover:underline">Tout marquer lu</button>
                </div>
                <div className="max-h-96 overflow-auto">
                  {notifs.length === 0 && <div className="px-4 py-6 text-sm text-slate-500 text-center">Aucune notification</div>}
                  {notifs.map((n) => (
                    <div key={n.id} className={`px-4 py-3 border-b border-border ${!n.read ? "bg-blue-50/50" : ""}`}>
                      <div className="text-sm font-semibold">{n.title}</div>
                      <div className="text-xs text-slate-600">{n.message}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </header>
        <main className="flex-1 p-4 lg:p-8 grain">{children}</main>
      </div>
    </div>
  );
}
